import { useEffect, useMemo, useState, Fragment } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import {
  Loader2, RefreshCw, Search, Filter, ShieldCheck, ChevronRight, FileDown,
  Copy, ExternalLink, User, Mail, Phone, Flame, Timer, Plus, Trash, X, Check,
  AlertOctagon, Calendar, FileSpreadsheet, Download,
} from "lucide-react";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import MultidocSummary from "@/components/MultidocSummary";
import { api, API } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { ConsistencyBadge } from "@/components/PremiumFinalCheck";

const CRM_STATUSES = [
  "Nuovo", "Da valutare", "Da ricontattare", "Ricontattato",
  "Appuntamento fissato", "Preventivo inviato", "Incarico acquisito",
  "Sospeso", "Archiviato", "Non assumibile",
];

const STATUS_COLOR = {
  "Nuovo": "bg-blue-100 text-blue-800 border-blue-300",
  "Da valutare": "bg-amber-100 text-amber-800 border-amber-300",
  "Da ricontattare": "bg-orange-100 text-orange-800 border-orange-300",
  "Ricontattato": "bg-cyan-100 text-cyan-800 border-cyan-300",
  "Appuntamento fissato": "bg-indigo-100 text-indigo-800 border-indigo-300",
  "Preventivo inviato": "bg-fuchsia-100 text-fuchsia-800 border-fuchsia-300",
  "Incarico acquisito": "bg-emerald-100 text-emerald-800 border-emerald-300",
  "Sospeso": "bg-slate-100 text-slate-600 border-slate-300",
  "Archiviato": "bg-slate-200 text-slate-500 border-slate-400",
  "Non assumibile": "bg-red-100 text-red-800 border-red-300",
};

const URGENCY_COLOR = {
  "Normale": "text-forensic-green",
  "Moderata": "text-forensic-amber",
  "Alta": "text-orange-700",
  "Critica": "text-forensic-red",
};

const LEAD_BAND_COLOR = {
  "Alto": "bg-forensic-green text-white",
  "Medio": "bg-forensic-amber text-white",
  "Basso": "bg-slate-400 text-white",
};

export default function AdminCrm() {
  const { user, isAdmin } = useAuth();
  const [rows, setRows] = useState([]);
  const [totals, setTotals] = useState({});
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [selected, setSelected] = useState(null);
  const [detail, setDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);

  // Filters
  const [q, setQ] = useState("");
  const [fStatus, setFStatus] = useState("all");
  const [fPremium, setFPremium] = useState("all");
  const [fRole, setFRole] = useState("all");
  const [fUrgency, setFUrgency] = useState("all");
  const [fWorkability, setFWorkability] = useState("all");
  const [fCtu, setFCtu] = useState("all");
  const [fCallback, setFCallback] = useState("all");
  const [fLead, setFLead] = useState("all");
  // B.3.2 — multidoc filters
  const [fNDocs, setFNDocs] = useState("all");
  const [fAssumMd, setFAssumMd] = useState("all");
  const [fScoreMd, setFScoreMd] = useState("all");
  const [fSupportMd, setFSupportMd] = useState("all");
  const [fServiceMd, setFServiceMd] = useState("all");
  const [fQualityMd, setFQualityMd] = useState("all");
  const [fAlertMd, setFAlertMd] = useState("all");
  // FASE C.3 — Consistency filter (blocking / warnings / coherent)
  const [fConsistency, setFConsistency] = useState("all");

  const allowed = user ? isAdmin : false;

  const load = async () => {
    setRefreshing(true);
    try {
      const r = await api.get("/admin/cases", { withCredentials: true });
      setRows(r.data.cases || []);
      setTotals(r.data.totals || {});
    } catch (e) {
      if (e?.response?.status === 403) toast.error("Solo per amministratori");
      else toast.error("Errore caricamento CRM");
    } finally { setLoading(false); setRefreshing(false); }
  };

  useEffect(() => {
    if (allowed) load(); else setLoading(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [allowed]);

  const loadDetail = async (caseId) => {
    setDetailLoading(true);
    try {
      const r = await api.get(`/admin/cases/${caseId}/detail`, { withCredentials: true });
      setDetail(r.data);
    } catch { toast.error("Errore caricamento dettaglio"); }
    finally { setDetailLoading(false); }
  };

  const openCase = (caseId) => { setSelected(caseId); loadDetail(caseId); };

  const filtered = useMemo(() => {
    const norm = (s) => (s || "").toString().toLowerCase();
    return rows.filter((r) => {
      if (q && !(
        norm(r.contact?.name).includes(norm(q)) ||
        norm(r.contact?.email).includes(norm(q)) ||
        norm(r.code).includes(norm(q))
      )) return false;
      if (fStatus !== "all" && r.crm_status !== fStatus) return false;
      if (fPremium === "yes" && !r.premium) return false;
      if (fPremium === "no" && r.premium) return false;
      if (fRole !== "all" && r.role !== fRole) return false;
      if (fUrgency !== "all" && r.urgency_level !== fUrgency) return false;
      if (fWorkability !== "all" && r.workability_level !== fWorkability) return false;
      if (fCtu === "yes" && !r.ctu) return false;
      if (fCtu === "no" && r.ctu) return false;
      if (fCallback === "yes" && !r.callback_requested) return false;
      if (fLead === "high" && r.lead_score < 70) return false;
      if (fLead === "mid" && (r.lead_score < 40 || r.lead_score >= 70)) return false;
      if (fLead === "low" && r.lead_score >= 40) return false;
      // B.3.2 — multidoc filters
      const ms = r.multidoc_summary;
      const nDocs = ms?.n_documents ?? r.questioned_documents_count ?? 0;
      if (fNDocs === "1" && nDocs !== 1) return false;
      if (fNDocs === "2" && nDocs !== 2) return false;
      if (fNDocs === "3plus" && nDocs < 3) return false;
      if (fNDocs === "5plus" && nDocs < 5) return false;
      if (ms) {
        if (fAssumMd === "all_assumable" && (ms.n_assumable !== ms.n_documents)) return false;
        if (fAssumMd === "any_reservation" && ms.n_with_reservation === 0) return false;
        if (fAssumMd === "any_not_assumable" && ms.n_not_assumable === 0) return false;
        if (fAssumMd === "insufficient" && ms.n_insufficient_data === 0) return false;
        if (fScoreMd === "any_under_50" && ms.score_min >= 50) return false;
        if (fScoreMd === "any_50_69" && !(ms.score_min <= 69 && ms.score_max >= 50)) return false;
        if (fScoreMd === "any_70_84" && !(ms.score_min <= 84 && ms.score_max >= 70)) return false;
        if (fScoreMd === "all_85plus" && ms.score_min < 85) return false;
        const sup = ms.supports || [];
        const os = ms.original_statuses || [];
        const isOrig = (s) => (s || "").toLowerCase() === "originale";
        const isScan = (s) => (s || "").toLowerCase().startsWith("scansione");
        const isCopy = (s) => (s || "").toLowerCase().startsWith("fotocopia");
        if (fSupportMd === "any_originale" && !sup.some(isOrig)) return false;
        if (fSupportMd === "no_originale" && sup.some(isOrig)) return false;
        if (fSupportMd === "any_scansione" && !sup.some(isScan)) return false;
        if (fSupportMd === "any_fotocopia" && !sup.some(isCopy)) return false;
        if (fSupportMd === "existing_not_available" && !os.includes("existing_not_available")) return false;
        if (fSupportMd === "existence_not_verified" && !os.includes("existence_not_verified")) return false;
        const svs = ms.services || [];
        if (fServiceMd === "consulenza" && !svs.some((s) => (s || "").startsWith("Consulenza"))) return false;
        if (fServiceMd === "parere" && !svs.some((s) => (s || "").startsWith("Parere"))) return false;
        if (fServiceMd === "perizia" && !svs.some((s) => (s || "").startsWith("Perizia"))) return false;
        const qs = ms.quality_statuses || [];
        if (fQualityMd !== "all" && !qs.includes(fQualityMd)) return false;
        if (fAlertMd === "any_clinical" && !ms.has_clinical_alert) return false;
        if (fAlertMd === "any_critical" && !ms.has_critical_alert) return false;
        if (fAlertMd === "none" && (ms.has_clinical_alert || ms.has_critical_alert)) return false;
      } else if (fNDocs !== "all" || fAssumMd !== "all" || fScoreMd !== "all"
                  || fSupportMd !== "all" || fServiceMd !== "all"
                  || fQualityMd !== "all" || fAlertMd !== "all") {
        // Multidoc filters active but this case has no multidoc_summary
        return false;
      }
      // FASE C.3 — Consistency filter
      if (fConsistency === "blocking" && r.consistency_status !== "inconsistent") return false;
      if (fConsistency === "warnings" && r.consistency_status !== "coherent_with_warnings") return false;
      if (fConsistency === "coherent" && r.consistency_status !== "coherent") return false;
      return true;
    });
  }, [rows, q, fStatus, fPremium, fRole, fUrgency, fWorkability, fCtu, fCallback, fLead,
      fNDocs, fAssumMd, fScoreMd, fSupportMd, fServiceMd, fQualityMd, fAlertMd, fConsistency]);

  const setStatus = async (caseId, status) => {
    try {
      await api.put(`/admin/cases/${caseId}/status`, { status }, { withCredentials: true });
      setRows((s) => s.map((r) => r.case_id === caseId ? { ...r, crm_status: status } : r));
      if (detail && detail.case?.id === caseId) loadDetail(caseId);
      toast.success(`Stato → ${status}`);
    } catch { toast.error("Errore cambio stato"); }
  };

  const downloadPdf = async (caseId, audience) => {
    try {
      const res = await fetch(`${API}/admin/cases/${caseId}/fascicolo-pdf?audience=${audience}`, { credentials: "include" });
      if (!res.ok) { toast.error("Errore download"); return; }
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url; a.download = `fascicolo-${audience}-${caseId.slice(0, 8)}.pdf`; a.click();
      URL.revokeObjectURL(url);
    } catch { toast.error("Errore"); }
  };

  const buildExportQuery = () => {
    const params = new URLSearchParams();
    if (q) params.set("q", q);
    if (fStatus !== "all") params.set("status", fStatus);
    if (fPremium !== "all") params.set("premium", fPremium);
    if (fRole !== "all") params.set("role", fRole);
    if (fUrgency !== "all") params.set("urgency", fUrgency);
    if (fWorkability !== "all") params.set("workability", fWorkability);
    if (fCtu !== "all") params.set("ctu", fCtu);
    if (fCallback !== "all") params.set("callback", fCallback);
    if (fLead !== "all") params.set("lead", fLead);
    return params.toString();
  };

  const exportCrm = async (format /* "csv" | "xlsx" */) => {
    const qs = buildExportQuery();
    const url = `${API}/admin/crm-export.${format}${qs ? `?${qs}` : ""}`;
    try {
      const res = await fetch(url, { credentials: "include" });
      if (!res.ok) { toast.error(`Errore export ${format.toUpperCase()}`); return; }
      const blob = await res.blob();
      const dlUrl = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = dlUrl;
      const today = new Date().toISOString().slice(0, 10).replace(/-/g, "");
      a.download = `crm-perizia-ready-${today}.${format}`;
      a.click();
      URL.revokeObjectURL(dlUrl);
      toast.success(`Export ${format.toUpperCase()} scaricato (${filtered.length} righe filtrate)`);
    } catch {
      toast.error("Errore export");
    }
  };

  if (allowed === false) {
    return (
      <div className="min-h-screen bg-paper flex flex-col">
        <Header />
        <main className="flex-1 flex items-center justify-center px-6">
          <div className="text-center max-w-md">
            <ShieldCheck className="text-slate-300 mx-auto" size={40} />
            <h1 className="font-serif text-2xl font-semibold mt-4">Accesso riservato</h1>
            <p className="text-sm text-slate-600 mt-2">CRM disponibile solo agli amministratori.</p>
            <Link to="/" className="inline-block mt-4 text-xs border hairline hover:border-navy px-4 py-2 rounded-sm">Torna alla home</Link>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-paper flex flex-col">
      <Header />
      <main className="flex-1 max-w-full mx-auto px-6 py-8 w-full">
        <div className="flex items-start justify-between flex-wrap gap-3 mb-4">
          <div>
            <div className="overline mb-1">Area amministratore</div>
            <h1 className="font-serif text-3xl lg:text-4xl font-semibold tracking-tight">CRM · Pratiche in gestione</h1>
            <p className="text-sm text-slate-600 mt-1">
              Tutti i fascicoli generati (gratuiti e premium) con stato, urgenza, lavorabilità, lead score e note interne.
            </p>
          </div>
          <button onClick={load} disabled={refreshing}
            className="inline-flex items-center gap-2 border hairline hover:border-navy px-4 py-2 rounded-sm text-sm disabled:opacity-60"
            data-testid="crm-refresh">
            {refreshing ? <Loader2 size={14} className="animate-spin" /> : <RefreshCw size={14} />} Aggiorna
          </button>
          <div className="flex items-center gap-2">
            <button onClick={() => exportCrm("xlsx")}
              className="inline-flex items-center gap-2 border hairline hover:border-forensic-green hover:text-forensic-green px-4 py-2 rounded-sm text-sm"
              data-testid="crm-export-xlsx"
              title="Esporta le pratiche filtrate in Excel (.xlsx)">
              <FileSpreadsheet size={14} /> Esporta Excel
            </button>
            <button onClick={() => exportCrm("csv")}
              className="inline-flex items-center gap-2 border hairline hover:border-navy hover:text-navy px-4 py-2 rounded-sm text-sm"
              data-testid="crm-export-csv"
              title="Esporta le pratiche filtrate in CSV">
              <Download size={14} /> Esporta CSV
            </button>
          </div>
        </div>

        {/* KPI totals */}
        <div className="grid md:grid-cols-4 gap-3 mb-4" data-testid="crm-totals">
          <Kpi label="Fascicoli totali" v={totals.count || 0} />
          <Kpi label="Premium acquistati" v={totals.premium || 0} accent="text-forensic-green" />
          <Kpi label="Con incoerenze bloccanti" v={totals.blocking_cases || 0} accent="text-forensic-red" />
          <Kpi label="Urgenza critica" v={totals.critical_urgency || 0} accent="text-forensic-red" />
        </div>

        {/* Filters */}
        <div className="border hairline bg-white p-3 mb-3">
          <div className="flex flex-wrap gap-2 items-center">
            <div className="relative">
              <Search size={13} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400" />
              <input value={q} onChange={(e) => setQ(e.target.value)}
                placeholder="Cerca nome, email o codice…"
                className="border hairline pl-7 pr-3 py-1.5 rounded-sm text-sm w-64" data-testid="crm-search" />
            </div>
            <FSel label="Stato" v={fStatus} onChange={setFStatus}
                  options={[{v: "all", l: "Tutti gli stati"}, ...CRM_STATUSES.map(s => ({v: s, l: s}))]} />
            <FSel label="Report" v={fPremium} onChange={setFPremium}
                  options={[{v: "all", l: "Tutti"}, {v: "yes", l: "Premium"}, {v: "no", l: "Gratuito"}]} />
            <FSel label="Ruolo" v={fRole} onChange={setFRole}
                  options={[{v: "all", l: "Tutti"}, {v: "privato", l: "Privato"}, {v: "avvocato", l: "Avvocato"}, {v: "professionista", l: "Professionista"}]} />
            <FSel label="Urgenza" v={fUrgency} onChange={setFUrgency}
                  options={[{v: "all", l: "Tutte"}, {v: "Normale", l: "Normale"}, {v: "Moderata", l: "Moderata"}, {v: "Alta", l: "Alta"}, {v: "Critica", l: "Critica"}]} />
            <FSel label="Lavorabilità" v={fWorkability} onChange={setFWorkability}
                  options={[{v: "all", l: "Tutte"}, {v: "Ottima", l: "Ottima"}, {v: "Buona", l: "Buona"}, {v: "Media", l: "Media"}, {v: "Limitata", l: "Limitata"}, {v: "Molto limitata", l: "Molto limitata"}]} />
            <FSel label="CTU" v={fCtu} onChange={setFCtu}
                  options={[{v: "all", l: "CTU: qualunque"}, {v: "yes", l: "CTU disposta"}, {v: "no", l: "Senza CTU"}]} />
            <FSel label="Ricontatto" v={fCallback} onChange={setFCallback}
                  options={[{v: "all", l: "Ricontatto: tutti"}, {v: "yes", l: "Ha richiesto contatto"}]} />
            <FSel label="Lead score" v={fLead} onChange={setFLead}
                  options={[{v: "all", l: "Lead: qualsiasi"}, {v: "high", l: "Alto (≥70)"}, {v: "mid", l: "Medio (40-69)"}, {v: "low", l: "Basso (<40)"}]} />
            {/* B.3.2 — Multidoc filters */}
            <FSel label="N. Documenti" v={fNDocs} onChange={setFNDocs}
                  options={[{v: "all", l: "N doc: tutti"}, {v: "1", l: "1"}, {v: "2", l: "2"}, {v: "3plus", l: "3+"}, {v: "5plus", l: "5+"}]} />
            <FSel label="Assumibilità (multidoc)" v={fAssumMd} onChange={setFAssumMd}
                  options={[{v: "all", l: "Assumibilità: tutti"},
                            {v: "all_assumable", l: "Tutti assumibili"},
                            {v: "any_reservation", l: "Almeno 1 con riserva"},
                            {v: "any_not_assumable", l: "Almeno 1 non assumibile"},
                            {v: "insufficient", l: "Dati insufficienti"}]} />
            <FSel label="Preparazione (per-doc)" v={fScoreMd} onChange={setFScoreMd}
                  options={[{v: "all", l: "Score: tutti"},
                            {v: "any_under_50", l: "Almeno 1 <50"},
                            {v: "any_50_69", l: "Almeno 1 50-69"},
                            {v: "any_70_84", l: "Almeno 1 70-84"},
                            {v: "all_85plus", l: "Tutti ≥85"}]} />
            <FSel label="Supporto (multidoc)" v={fSupportMd} onChange={setFSupportMd}
                  options={[{v: "all", l: "Supporto: tutti"},
                            {v: "any_originale", l: "Almeno 1 originale"},
                            {v: "no_originale", l: "Nessun originale"},
                            {v: "any_scansione", l: "Almeno 1 scansione"},
                            {v: "any_fotocopia", l: "Almeno 1 fotocopia"},
                            {v: "existing_not_available", l: "Originale non disponibile"},
                            {v: "existence_not_verified", l: "Esistenza non verificata"}]} />
            <FSel label="Servizio (multidoc)" v={fServiceMd} onChange={setFServiceMd}
                  options={[{v: "all", l: "Servizio: tutti"},
                            {v: "consulenza", l: "Consulenza orale preliminare"},
                            {v: "parere", l: "Parere sintetico"},
                            {v: "perizia", l: "Perizia grafologica completa"}]} />
            <FSel label="Quality status" v={fQualityMd} onChange={setFQualityMd}
                  options={[{v: "all", l: "Quality: tutti"},
                            {v: "adeguata", l: "Adeguata"},
                            {v: "parziale", l: "Parziale"},
                            {v: "insufficiente", l: "Insufficiente"},
                            {v: "non_completamente_valutabile", l: "Non compl. valutabile"}]} />
            <FSel label="Alert (multidoc)" v={fAlertMd} onChange={setFAlertMd}
                  options={[{v: "all", l: "Alert: tutti"},
                            {v: "any_clinical", l: "Almeno 1 clinico"},
                            {v: "any_critical", l: "Almeno 1 critico"},
                            {v: "none", l: "Nessun alert"}]} />
            <FSel label="Coerenza" v={fConsistency} onChange={setFConsistency}
                  options={[{v: "all", l: "Coerenza: tutte"},
                            {v: "blocking", l: "Incoerenze da risolvere"},
                            {v: "warnings", l: "Da verificare"},
                            {v: "coherent", l: "Coerente"}]} />
            <button type="button" onClick={() => {
              setFNDocs("all"); setFAssumMd("all"); setFScoreMd("all"); setFSupportMd("all");
              setFServiceMd("all"); setFQualityMd("all"); setFAlertMd("all");
            }} className="text-[10px] uppercase tracking-widest border hairline px-2 py-1 hover:bg-slate-50"
               data-testid="crm-reset-multidoc-filters">Reset multidoc</button>
            <span className="text-xs text-slate-500 font-mono ml-auto" data-testid="crm-results-count">
              {filtered.length} / {rows.length} risultati
            </span>
          </div>
        </div>

        {/* Table */}
        <div className="border hairline bg-white overflow-auto" data-testid="crm-container">
          {loading ? (
            <div className="p-12 text-center"><Loader2 className="animate-spin text-navy mx-auto" /></div>
          ) : filtered.length === 0 ? (
            <div className="p-12 text-center text-slate-500 text-sm">Nessuna pratica corrispondente ai filtri.</div>
          ) : (
            <table className="w-full text-sm min-w-[1200px]">
              <thead>
                <tr className="bg-navy text-white text-[10px] uppercase tracking-widest">
                  <th className="px-3 py-3 text-left">Codice</th>
                  <th className="px-3 py-3 text-left">Cliente</th>
                  <th className="px-3 py-3 text-left">Caso</th>
                  <th className="px-3 py-3 text-center">Indice / Docs</th>
                  <th className="px-3 py-3 text-center">Urgenza</th>
                  <th className="px-3 py-3 text-center">Lavor.</th>
                  <th className="px-3 py-3 text-center">Lead</th>
                  <th className="px-3 py-3 text-center">Premium</th>
                  <th className="px-3 py-3 text-center">Coerenza</th>
                  <th className="px-3 py-3 text-left">Stato</th>
                  <th className="px-3 py-3"></th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((r) => (
                  <tr key={r.case_id} className="border-t hairline hover:bg-slate-50 cursor-pointer"
                    onClick={() => openCase(r.case_id)} data-testid={`crm-row-${r.case_id}`}>
                    <td className="px-3 py-2 font-mono text-[11px] text-slate-600">
                      <div>{r.code}</div>
                      <div className="text-slate-400 text-[10px]">{r.created_at ? new Date(r.created_at).toLocaleString("it-IT", { timeZone: "Europe/Rome", day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" }) : ""}</div>
                    </td>
                    <td className="px-3 py-2">
                      <div className="font-medium">{r.contact?.name || <span className="text-slate-400">—</span>}</div>
                      <div className="flex gap-2 items-center text-[11px] text-slate-500 font-mono">
                        {r.contact?.email && <span className="flex items-center gap-1"><Mail size={9} />{r.contact.email}</span>}
                      </div>
                    </td>
                    <td className="px-3 py-2 text-xs">
                      <div>{r.document_type || "—"}</div>
                      <div className="text-slate-500">{r.role} · {r.phase || "—"}{r.ctu ? " · CTU" : ""}</div>
                    </td>
                    <td className="px-3 py-2 text-center">
                      {(r.multidoc_summary?.n_documents || 0) >= 2 ? (
                        <div className="text-xs" data-testid={`crm-row-${r.case_id}-multidoc`}>
                          <div className="font-semibold text-navy">{r.multidoc_summary.n_documents} doc</div>
                          <div className="text-slate-600 font-mono">{r.multidoc_summary.score_min}–{r.multidoc_summary.score_max}</div>
                          <div className="text-[10px] text-slate-500 mt-0.5">
                            {r.multidoc_summary.n_assumable}A / {r.multidoc_summary.n_with_reservation}R
                            {r.multidoc_summary.n_not_assumable > 0 && ` / ${r.multidoc_summary.n_not_assumable}N`}
                          </div>
                        </div>
                      ) : (
                        <div className="font-serif text-lg font-semibold">{r.score_total}</div>
                      )}
                    </td>
                    <td className={`px-3 py-2 text-center text-xs font-semibold ${URGENCY_COLOR[r.urgency_level] || ""}`}>
                      {r.urgency_level}
                    </td>
                    <td className="px-3 py-2 text-center text-xs">{r.workability_level}</td>
                    <td className="px-3 py-2 text-center">
                      <span className={`inline-block px-2 py-0.5 rounded-full font-mono text-xs ${LEAD_BAND_COLOR[r.lead_band] || ""}`}>
                        {r.lead_score}
                      </span>
                    </td>
                    <td className="px-3 py-2 text-center">
                      {r.premium ? <Check size={14} className="text-forensic-green inline" /> : <span className="text-slate-300">—</span>}
                    </td>
                    <td className="px-3 py-2 text-center" data-testid={`crm-row-${r.case_id}-consistency`}>
                      <ConsistencyBadge status={r.consistency_status} />
                      {(r.consistency_counts?.blocking > 0) && (
                        <div className="text-[10px] text-forensic-red font-mono mt-1">
                          {r.consistency_counts.blocking} bloccant{r.consistency_counts.blocking === 1 ? "e" : "i"}
                        </div>
                      )}
                    </td>
                    <td className="px-3 py-2">
                      <span className={`inline-block px-2 py-0.5 rounded-sm border text-[10px] uppercase tracking-widest ${STATUS_COLOR[r.crm_status] || ""}`}>
                        {r.crm_status}
                      </span>
                    </td>
                    <td className="px-3 py-2 text-right">
                      <ChevronRight size={14} className="text-slate-400" />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </main>

      {/* SIDE PANEL detail */}
      {selected && (
        <aside className="fixed top-0 right-0 bottom-0 w-full max-w-2xl bg-white shadow-2xl z-40 overflow-y-auto border-l-2 border-navy"
               data-testid="crm-detail">
          <div className="p-6 border-b hairline sticky top-0 bg-white z-10 flex justify-between items-start">
            <div>
              <div className="overline">Dettaglio pratica</div>
              <div className="font-mono text-xs text-slate-500 mt-1">#{selected}</div>
            </div>
            <button onClick={() => { setSelected(null); setDetail(null); }}
              className="text-slate-400 hover:text-forensic-red" data-testid="crm-close-detail">
              <X size={18} />
            </button>
          </div>
          {detailLoading || !detail ? (
            <div className="p-12 text-center"><Loader2 className="animate-spin text-navy mx-auto" /></div>
          ) : (
            <DetailPanel d={detail} setStatus={(s) => setStatus(selected, s)} downloadPdf={downloadPdf}
                         onReload={() => loadDetail(selected)} />
          )}
        </aside>
      )}
      <Footer />
    </div>
  );
}

function DetailPanel({ d, setStatus, downloadPdf, onReload }) {
  const { case: c, score, urgency, workability, lead_score, actions, crm_notes, crm_events, crm_status, next_service, premium, questionedDocumentAssessment } = d;
  const [noteText, setNoteText] = useState("");
  const [addingNote, setAddingNote] = useState(false);
  const [addingEvent, setAddingEvent] = useState(false);
  const [newEvent, setNewEvent] = useState({ label: "", date: "", category: "manual", notes: "" });
  const assessments = questionedDocumentAssessment || [];
  const isMultidoc = assessments.length >= 2;

  const addNote = async () => {
    if (!noteText.trim()) return;
    setAddingNote(true);
    try {
      // FASE E.2 Turno 3.1 HOTFIX DEFINITIVO — legacy CRM notes moved to
      // /admin/cases/{id}/crm-notes to eliminate route collision with the
      // E.2 consultant_notes endpoint (POST /admin/cases/{id}/notes).
      await api.post(`/admin/cases/${c.id}/crm-notes`, { text: noteText }, { withCredentials: true });
      setNoteText("");
      onReload();
      toast.success("Nota aggiunta");
    } catch { toast.error("Errore"); }
    finally { setAddingNote(false); }
  };

  const delNote = async (noteId) => {
    if (!window.confirm("Confermi eliminazione della nota?")) return;
    try {
      await api.delete(`/admin/cases/${c.id}/crm-notes/${noteId}`, { withCredentials: true });
      onReload();
      toast.success("Nota eliminata");
    } catch { toast.error("Errore"); }
  };

  const addEvent = async () => {
    if (!newEvent.label.trim()) return;
    try {
      await api.post(`/admin/cases/${c.id}/timeline`, newEvent, { withCredentials: true });
      setAddingEvent(false); setNewEvent({ label: "", date: "", category: "manual", notes: "" });
      onReload();
      toast.success("Evento aggiunto");
    } catch { toast.error("Errore"); }
  };

  // Distinct role displays (new schema + legacy fallback)
  const requesterFull = [c.requester?.title, c.requester?.first_name, c.requester?.last_name].filter(Boolean).join(" ").trim() || c.contact_name || "Cliente anonimo";
  const requesterOrg = c.requester?.organization || "";
  const assistedFull = [c.assisted_party?.first_name, c.assisted_party?.last_name].filter(Boolean).join(" ").trim();
  const assistedRole = c.assisted_party?.role === "altro" ? (c.assisted_party?.role_other || "") : (c.assisted_party?.role || "");
  const writerFull = [c.identification?.subject?.first_name, c.identification?.subject?.last_name].filter(Boolean).join(" ").trim() || c.identification?.subject?.name || "";
  const caseDesc = c.case_short_description || c.case_description || "";

  return (
    <div className="p-6 space-y-5">
      {/* Header client — multidoc-aware title */}
      <div>
        {isMultidoc ? (
          <>
            <div className="font-serif text-xl font-semibold" data-testid="detail-title-multidoc">
              {assistedFull && writerFull ? `${assistedFull} c. ${writerFull}` : (assistedFull || writerFull || requesterFull)}
              {" — "}
              <span className="text-slate-600">{assessments.length} documenti in verifica</span>
            </div>
            <div className="text-xs text-slate-600 mt-0.5" data-testid="detail-requester">{requesterFull}{requesterOrg ? ` — ${requesterOrg}` : ""}</div>
          </>
        ) : (
          <>
            <div className="font-serif text-xl font-semibold" data-testid="detail-requester">{requesterFull}</div>
            {requesterOrg && (
              <div className="text-xs text-slate-600 mt-0.5" data-testid="detail-organization">{requesterOrg}</div>
            )}
          </>
        )}
        <div className="text-sm text-slate-600 flex gap-3 mt-1 flex-wrap">
          {(c.requester?.email || c.contact_email) && <a href={`mailto:${c.requester?.email || c.contact_email}`} className="hover:text-navy flex items-center gap-1"><Mail size={12} />{c.requester?.email || c.contact_email}</a>}
          {(c.requester?.phone || c.contact_phone) && <a href={`tel:${c.requester?.phone || c.contact_phone}`} className="hover:text-navy flex items-center gap-1"><Phone size={12} />{c.requester?.phone || c.contact_phone}</a>}
          <span className="flex items-center gap-1 text-slate-500"><User size={12} />{c.role}</span>
        </div>
        {(assistedFull || writerFull) && (
          <div className="mt-2 flex flex-wrap gap-4 text-xs text-slate-600">
            {assistedFull && (
              <span data-testid="detail-assisted">
                <b>Assistito:</b> {assistedFull}{assistedRole ? ` — ${assistedRole}` : ""}
              </span>
            )}
            {writerFull && (
              <span data-testid="detail-writer">
                <b>Scrivente:</b> {writerFull}
              </span>
            )}
          </div>
        )}
        {caseDesc && (
          <details className="mt-3 border-l-2 border-navy bg-navy/5 px-3 py-2 text-xs text-slate-700" data-testid="detail-case-description">
            <summary className="cursor-pointer text-navy font-medium">Breve descrizione del caso</summary>
            <p className="mt-2 whitespace-pre-wrap leading-snug">{caseDesc}</p>
          </details>
        )}
      </div>

      {/* Actions */}
      <div className="flex flex-wrap gap-2">
        <select value={crm_status} onChange={(e) => setStatus(e.target.value)} data-testid="detail-status-select"
                className="border hairline px-3 py-2 rounded-sm text-sm bg-white">
          {CRM_STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
        </select>
        <button onClick={() => downloadPdf(c.id, "avvocato")}
          className="inline-flex items-center gap-1 bg-navy hover:bg-navy-dark text-white text-xs px-3 py-2 rounded-sm" data-testid="detail-dl-avv">
          <FileDown size={12} /> PDF avvocato
        </button>
        <button onClick={() => downloadPdf(c.id, "privato")}
          className="inline-flex items-center gap-1 border border-navy text-navy hover:bg-navy hover:text-white text-xs px-3 py-2 rounded-sm">
          <FileDown size={12} /> PDF privato
        </button>
        <Link to={`/case/${c.id}`} target="_blank"
          className="inline-flex items-center gap-1 border hairline hover:border-navy text-xs px-3 py-2 rounded-sm">
          <ExternalLink size={12} /> Apri
        </Link>
        {/* FASE E.2 Turno 3 — CTA modifica / integra fascicolo (revision editor) */}
        <Link to={`/admin/case/${c.id}/edit`}
          className="inline-flex items-center gap-1 border-2 border-navy text-navy hover:bg-navy hover:text-white text-xs px-3 py-2 rounded-sm font-medium transition-colors"
          data-testid="detail-edit-case">
          <Filter size={12} /> Modifica / integra fascicolo
        </Link>
      </div>

      {/* KPIs — multidoc-aware */}
      {isMultidoc && (d.multidoc_summary || assessments.length > 0) && (
        <div className="border-2 border-navy bg-navy/5 p-4" data-testid="detail-multidoc-summary">
          <div className="overline mb-2">Stato documentale (multidocumento)</div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-xs">
            <div><b>{assessments.length}</b> documenti in verifica</div>
            <div>Range: <b>{Math.min(...assessments.map(a => a.preparation_score))}–{Math.max(...assessments.map(a => a.preparation_score))}</b>/100</div>
            <div>Assumibili: <b>{assessments.filter(a => a.assumability === "Assumibile").length}</b></div>
            <div>Con riserva: <b>{assessments.filter(a => a.assumability === "Assumibile con riserva").length}</b></div>
            <div>Non assumibili: <b>{assessments.filter(a => a.assumability === "Non ancora assumibile").length}</b></div>
            <div>Servizi provvisori: <b>{assessments.filter(a => a.service_recommendation_provisional).length}</b> / {assessments.length}</div>
            <div>Comparative pool: <b>{(c.comparatives?.comparatives || []).length}</b></div>
            <div>Alert (eventi distinti): <b>{new Set(assessments.flatMap(a => (a.alerts || []).filter(al => al.category === "sanitario").map(al => `${al.event_date}|${al.event_label}`))).size}</b></div>
          </div>
          {(() => {
            const ASSUM_RANK = { "Non ancora assumibile": 0, "Dati insufficienti": 1, "Assumibile con riserva": 2, "Assumibile": 3 };
            const SUPP_RANK = { "fotografia": 0, "fotocopia_bn": 0, "fotocopia_colori": 1, "scansione_bn": 2, "scansione_colori": 3, "originale": 4 };
            const mc = [...assessments].sort((a, b) => {
              const ka = [ASSUM_RANK[a.assumability] ?? 5, a.preparation_score, SUPP_RANK[(a.support_type || "").toLowerCase()] ?? 5];
              const kb = [ASSUM_RANK[b.assumability] ?? 5, b.preparation_score, SUPP_RANK[(b.support_type || "").toLowerCase()] ?? 5];
              for (let i = 0; i < ka.length; i++) if (ka[i] !== kb[i]) return ka[i] - kb[i];
              return 0;
            })[0];
            return mc ? (
              <div className="mt-3 pt-2 border-t border-navy/30 text-xs" data-testid="detail-most-critical">
                <b>Documento più critico:</b> DOC #{mc.document_index.toString().padStart(2, "0")} — {mc.document_title} · {mc.preparation_score}/100 · {mc.workability} · {mc.assumability}
              </div>
            ) : null;
          })()}
        </div>
      )}

      {/* Multidoc per-document accordion */}
      {isMultidoc && (
        <div data-testid="detail-multidoc-cards">
          <MultidocSummary assessments={assessments} title="Documenti in verifica" defaultExpanded={false} />
        </div>
      )}

      {/* KPIs — legacy single-doc only */}
      {!isMultidoc && (
      <div className="grid grid-cols-4 gap-2 text-center">
        <div className="border hairline p-3">
          <div className="text-[10px] uppercase tracking-widest text-slate-500">Indice</div>
          <div className="font-serif text-2xl font-semibold mt-1">{score.total}</div>
        </div>
        <div className="border hairline p-3">
          <div className="text-[10px] uppercase tracking-widest text-slate-500">Urgenza</div>
          <div className={`font-semibold text-sm mt-1 ${URGENCY_COLOR[urgency.level] || ""}`}>{urgency.level}</div>
        </div>
        <div className="border hairline p-3">
          <div className="text-[10px] uppercase tracking-widest text-slate-500">Lavorabilità</div>
          <div className="font-semibold text-sm mt-1">{workability.level}</div>
        </div>
        <div className="border hairline p-3">
          <div className="text-[10px] uppercase tracking-widest text-slate-500">Lead</div>
          <div className="font-mono font-bold mt-1">{lead_score.score}</div>
        </div>
      </div>
      )}

      {/* Lead factors */}
      <div className="border hairline p-3 bg-slate-50">
        <div className="overline mb-2 flex items-center gap-1"><Flame size={11} /> Fattori lead score</div>
        {lead_score.factors.length === 0 ? (
          <p className="text-xs text-slate-500 italic">Nessun fattore rilevato.</p>
        ) : (
          <ul className="space-y-1 text-xs">
            {lead_score.factors.map((f, i) => (
              <li key={i} className="flex justify-between">
                <span>{f.label}</span>
                <span className="font-mono text-forensic-green">+{f.points}</span>
              </li>
            ))}
          </ul>
        )}
      </div>

      {/* Urgency & Workability reasons */}
      <div className="grid md:grid-cols-2 gap-3">
        <ReasonsBox title="Urgenza — motivazioni" items={urgency.reasons} />
        <ReasonsBox title="Lavorabilità — motivazioni" items={workability.reasons} />
      </div>

      {/* Next service */}
      {next_service && (
        <div className="border-l-2 border-navy bg-navy/5 p-3">
          <div className="overline mb-1">Servizio potenzialmente pertinente</div>
          <div className="font-semibold">{next_service.title}</div>
          <p className="text-xs text-slate-600 mt-1">{next_service.detail}</p>
        </div>
      )}

      {/* Improvement actions */}
      {actions && actions.length > 0 && (
        <div>
          <div className="overline mb-2">Come migliorare il fascicolo</div>
          <ul className="space-y-2">
            {actions.map((a, i) => (
              <li key={i} className="border hairline p-3">
                <div className="font-semibold text-sm">{a.title}</div>
                <p className="text-xs text-slate-600 mt-1">{a.detail}</p>
                <p className="text-xs font-mono text-forensic-green mt-1">{a.estimated_delta}</p>
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Internal notes */}
      <div>
        <div className="flex items-center justify-between mb-2">
          <div className="overline">Note interne ({crm_notes?.length || 0})</div>
        </div>
        <div className="border hairline p-3 space-y-2 bg-slate-50">
          {(crm_notes || []).length === 0 && (
            <p className="text-xs text-slate-500 italic">Nessuna nota ancora inserita.</p>
          )}
          {(crm_notes || []).slice().reverse().map((n) => (
            <div key={n.id} className="border hairline bg-white p-2" data-testid={`note-${n.id}`}>
              <div className="flex items-start justify-between gap-2">
                <div className="text-xs text-slate-500 font-mono">
                  {new Date(n.at).toLocaleString("it-IT")} · {n.by || "system"}
                </div>
                <button onClick={() => delNote(n.id)} className="text-slate-400 hover:text-forensic-red" data-testid={`del-note-${n.id}`}>
                  <Trash size={11} />
                </button>
              </div>
              <p className="text-sm mt-1 whitespace-pre-wrap">{n.text}</p>
            </div>
          ))}
          <div className="mt-2 flex gap-2">
            <textarea value={noteText} onChange={(e) => setNoteText(e.target.value)}
              rows={2} placeholder="Aggiungi nota interna (solo admin)…"
              className="flex-1 border hairline px-2 py-1 rounded-sm text-sm bg-white" data-testid="add-note-textarea" />
            <button onClick={addNote} disabled={addingNote || !noteText.trim()}
              className="bg-navy hover:bg-navy-dark text-white text-xs px-3 py-2 rounded-sm disabled:opacity-60" data-testid="add-note-btn">
              Aggiungi
            </button>
          </div>
        </div>
      </div>

      {/* Timeline */}
      <div>
        <div className="flex items-center justify-between mb-2">
          <div className="overline">Timeline eventi</div>
          <button onClick={() => setAddingEvent(!addingEvent)}
            className="text-xs text-navy hover:text-navy-dark inline-flex items-center gap-1" data-testid="add-event-toggle">
            <Plus size={11} /> Evento manuale
          </button>
        </div>
        <div className="border hairline p-3 space-y-2 bg-slate-50">
          {addingEvent && (
            <div className="border hairline bg-white p-2 grid grid-cols-[6rem_1fr_auto] gap-2">
              <input type="date" value={newEvent.date} onChange={(e) => setNewEvent({...newEvent, date: e.target.value})}
                     className="border hairline px-2 py-1 rounded-sm text-xs font-mono" />
              <input type="text" placeholder="Descrizione" value={newEvent.label}
                     onChange={(e) => setNewEvent({...newEvent, label: e.target.value})}
                     className="border hairline px-2 py-1 rounded-sm text-xs" />
              <button onClick={addEvent} className="bg-navy text-white text-xs px-3 py-1 rounded-sm">Salva</button>
            </div>
          )}
          {(crm_events || []).slice().reverse().map((ev) => (
            <div key={ev.id} className="border-l-2 border-slate-300 pl-3 py-1" data-testid={`event-${ev.id}`}>
              <div className="flex justify-between items-baseline">
                <span className="text-sm font-medium">
                  {ev.type === "case_created" && "Fascicolo creato"}
                  {ev.type === "status_change" && `Stato: ${ev.from || "—"} → ${ev.to}`}
                  {ev.type === "note_added" && "Nota interna aggiunta"}
                  {ev.type === "note_deleted" && "Nota eliminata"}
                  {ev.type === "premium_purchased" && "Report Premium acquistato"}
                  {ev.type === "timeline_added" && `Evento: ${ev.label}`}
                </span>
                <span className="text-[10px] font-mono text-slate-500">
                  {new Date(ev.at).toLocaleString("it-IT")}
                </span>
              </div>
              {ev.by && <div className="text-[10px] text-slate-400 font-mono">{ev.by}</div>}
              {ev.note && <div className="text-xs text-slate-500 italic mt-1">{ev.note}</div>}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function ReasonsBox({ title, items }) {
  return (
    <div className="border hairline p-3 bg-white">
      <div className="overline mb-2">{title}</div>
      <ul className="text-xs text-slate-700 space-y-1">
        {(items || []).map((r, i) => <li key={i} className="leading-snug">· {r}</li>)}
      </ul>
    </div>
  );
}

function Kpi({ label, v, accent = "text-navy" }) {
  return (
    <div className="border hairline bg-white p-4">
      <div className="overline">{label}</div>
      <div className={`font-serif text-3xl font-semibold mt-1 ${accent}`}>{v}</div>
    </div>
  );
}

function FSel({ label, v, onChange, options }) {
  return (
    <select value={v} onChange={(e) => onChange(e.target.value)}
            className="border hairline px-2 py-1.5 rounded-sm text-xs bg-white" data-testid={`filter-${label.toLowerCase().replace(/\s/g, "-")}`}>
      {options.map((o) => <option key={o.v} value={o.v}>{o.l}</option>)}
    </select>
  );
}
